/* eslint-disable @typescript-eslint/prefer-nullish-coalescing */
import type { AxiosInstance } from 'axios';
import { hasRefreshToken } from './auth-interceptor-shared';
import { terminalLogout } from './terminal-logout';
import { webviewHttpClient as mHttpClient } from '@/app/[locale]/(m)/components/auth/webview-http-client';
import { webviewHttpClient as orderHttpClient } from '@/app/[locale]/(order)/components/auth/webview-http-client';

/**
 * 401 handling cho các API đi qua `webviewHttpClient` (đặt đơn, ước tính phí, lưu
 * session, địa chỉ, rating...).
 *
 * Vì sao cần file này: các API này KHÔNG đi qua `publicHttpClient` nên KHÔNG được
 * web interceptor (install-axios-interceptor.ts) bảo vệ. Nếu không có nó, WEB user
 * token hết hạn → order/estimate/địa chỉ trả 401 nhưng không ai bắt → mutation
 * reject → toast sai → user KẸT (không đặt được đơn, không bị đăng xuất).
 *
 * Hành vi (CHỈ web — có cookie refresh_token):
 *  - 401 (token JWT hết hạn) → terminalLogout (clear + toast + về /). KHÔNG refresh:
 *    token web sống 3 ngày, refresh runtime đã bỏ (từng gây vòng lặp authen khi
 *    refresh lỗi). 401 = phiên hết hạn thật → đăng xuất dứt điểm.
 *  - Webinapp (KHÔNG có refresh_token) → KHÔNG đụng tới (reject như cũ → giữ phiên,
 *    đúng thiết kế webinapp; user mở lại từ app để lấy token mới).
 *
 * Có 2 instance webviewHttpClient riêng biệt ((m) và (order)) — cài trên cả hai.
 * Idempotent với web interceptor: chỉ act khi hasRefreshToken() = true; còn luồng FB
 * webview (WebviewSessionProvider) dùng session access_token (không có refresh_token)
 * nên nhánh này tự bỏ qua, không xung đột.
 */

let installed = false;

export function installWebviewAuthInterceptor() {
  if (installed || typeof window === 'undefined') return;
  installed = true;
  attach401Logout(mHttpClient);
  attach401Logout(orderHttpClient);
}

function attach401Logout(client: AxiosInstance) {
  client.interceptors.response.use(
    (res) => res,
    async (error) => {
      const status = error?.response?.status;
      const original = error?.config;
      // Chỉ xử lý 401 của WEB session (có refresh_token). Webinapp/FB-webview
      // (không có refresh_token) giữ phiên như cũ.
      if (status !== 401 || !original || !hasRefreshToken()) {
        return Promise.reject(error);
      }

      // Token JWT hết hạn → đăng xuất (không retry, không refresh).
      terminalLogout();
      return Promise.reject(error);
    }
  );
}
